<template>
  <v-row>
    <v-col cols="12">
      <base-card>
        <v-card-title class="production-title">
          <div class="production-heading">
            <div class="card-title mb-0">
              Production
            </div>

            <div class="production-subtitle">
              Templates ready for production usage
            </div>
          </div>

          <v-spacer />

          <div class="production-actions">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              placeholder="Search templates, production title, channels..."
              single-line
              dense
              hide-details
              class="production-search"
              clearable
            />

            <v-select
              v-model="selectedChannelFilterId"
              :items="channelFilterOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              clearable
              class="production-channel-filter"
            />
          </div>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="groupedItems"
          :search="search"
          :loading="loading"
          group-by="productionGroup"
          item-key="id"
          class="production-table"
          disable-pagination
          hide-default-footer
          @click:row="goToTemplateDetails"
        >
          <template v-slot:group.header="{ group, headers, toggle, isOpen }">
            <td
              :colspan="headers.length"
              class="production-group-cell"
            >
              <button
                type="button"
                class="production-group-toggle"
                @click.stop="toggle"
              >
                <v-icon
                  small
                  class="mr-2"
                >
                  {{ isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                </v-icon>

                <span>{{ getProductionGroupLabel(group) }}</span>
              </button>
            </td>
          </template>

          <!-- ID -->
          <template v-slot:item.idView="{ item }">
            <div class="template-id">
              {{ item.id }}
            </div>
          </template>

          <!-- Template -->
          <template v-slot:item.templateSearch="{ item }">
            <div class="template-cell">
              <div class="template-name">
                {{ item.name }}
              </div>

              <div class="template-meta">
                <span class="template-type">
                  {{ formatProductionType(item.prodType) }}
                </span>
                <span class="template-meta-separator">·</span>
                <span class="template-layers">
                  {{ getLayersCount(item) }} layers
                </span>
              </div>

              <div
                v-if="hasPriority(item)"
                class="template-priority"
              >
                Priority {{ item.priority }} — {{ formatPriority(item.priority) }}
              </div>
            </div>
          </template>

          <!-- Preview -->
          <template v-slot:item.previewSearch="{ item }">
            <div
              class="preview-cell"
              @click.stop
              @mouseenter="playPreview($event, item)"
              @mouseleave="stopPreview($event, item)"
            >
              <div class="preview-frame">
                <video
                  v-if="getPreviewVideo(item)"
                  :src="getPreviewVideo(item)"
                  :poster="getPreviewPoster(item)"
                  muted
                  playsinline
                  preload="metadata"
                  class="preview-video"
                />

                <v-img
                  v-else-if="getPreviewPoster(item)"
                  :src="getPreviewPoster(item)"
                  class="preview-image"
                  contain
                />

                <div
                  v-else
                  class="preview-empty"
                >
                  <v-icon size="28">
                    mdi-video-off-outline
                  </v-icon>
                </div>

                <img
                  v-if="getPreviewPoster(item) && !item.previewPlaying"
                  :src="getPreviewPoster(item)"
                  class="preview-poster"
                  alt=""
                >

                <div
                  v-if="getPreviewVideo(item) && !item.previewPlaying"
                  class="preview-play"
                >
                  <v-icon
                    color="white"
                    size="22"
                  >
                    mdi-play
                  </v-icon>
                </div>
              </div>

              <div class="preview-duration">
                {{ item.durationSeconds }} sec
              </div>
            </div>
          </template>

          <!-- Production title / description -->
          <template v-slot:item.productionSearch="{ item }">
            <div class="production-copy">
              <div class="production-copy-title">
                {{ item.prodTitle }}
              </div>

              <div
                v-if="item.prodDescription"
                class="production-copy-desc"
              >
                {{ item.prodDescription }}
              </div>

              <div
                v-else
                class="production-copy-empty"
              >
                No production description
              </div>
            </div>
          </template>

          <!-- Channels -->
          <template v-slot:item.channelsText="{ item }">
            <div
              class="channels-cell"
              @click.stop
            >
              <template v-if="item.channels.length">
                <v-chip
                  v-for="channel in getVisibleChannels(item)"
                  :key="channelKey(channel)"
                  small
                  class="channel-chip"
                  :class="{ 'channel-chip--selected': channel.selected }"
                >
                  <v-icon
                    v-if="channel.suspended"
                    left
                    class='ml-1'
                    x-small
                    color="warning"
                  >
                    mdi-pause
                  </v-icon>

                  {{ channel.name }}
                </v-chip>

                <v-menu
                  v-if="getHiddenChannels(item).length"
                  offset-y
                  max-width="360"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      small
                      class="channel-chip channel-chip-more"
                      v-bind="attrs"
                      v-on="on"
                    >
                      +{{ getHiddenChannels(item).length }}
                    </v-chip>
                  </template>

                  <v-card>
                    <v-list dense>
                      <v-list-item
                        v-for="channel in getHiddenChannels(item)"
                        :key="channelKey(channel)"
                        :class="{ 'channel-list-item--selected': channel.selected }"
                      >
                        <v-list-item-content>
                          <v-list-item-title>
                            <v-icon
                              v-if="channel.selected"
                              small
                              color="success"
                              class="mr-1"
                            >
                              mdi-check-circle
                            </v-icon>

                            <v-icon
                              v-if="channel.suspended"
                              small
                              color="warning"
                              class="mr-1"
                            >
                              mdi-pause-circle-outline
                            </v-icon>

                            {{ channel.name }}
                          </v-list-item-title>

                          <v-list-item-subtitle v-if="channel.extid">
                            {{ channel.extid }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </template>

              <span
                v-else
                class="channels-empty"
              >
                No channels
              </span>
            </div>
          </template>
        </v-data-table>
      </base-card>
    </v-col>
  </v-row>
</template>

<script>
import { processAEPProject } from '@/store/converters'

export default {
  name: 'Production',
  metaInfo: {
    title: 'Production',
  },
  data () {
    return {
      search: '',
      selectedChannelFilterId: 'all',
      items: [],
      channels: [],
      pageLoading: false,
      headers: [
        {
          text: 'ID',
          value: 'idView',
          sortable: true,
          align: 'start',
          width: '70',
        },
        {
          text: 'Template',
          value: 'templateSearch',
          align: 'start',
          sortable: false,
          width: '210',
        },
        {
          text: 'Preview',
          value: 'previewSearch',
          sortable: false,
          width: '190',
        },
        {
          text: 'Production',
          value: 'productionSearch',
          sortable: false,
        },
        {
          text: 'Channels',
          value: 'channelsText',
          sortable: false,
          width: '420',
        },
      ],
    }
  },
  computed: {
    loading () {
      return this.$store.getters.isDataLoaded || this.pageLoading
    },

    channelFilterOptions () {
      const options = [
        {
          text: 'All channels',
          value: 'all',
        },
      ]

      for (let i = 0; i < this.channels.length; i += 1) {
        const channel = this.channels[i]

        options.push({
          text: channel.name || channel.extid || `Channel #${channel.id}`,
          value: channel.id,
        })
      }

      return options
    },

    filteredItems () {
      if (!this.selectedChannelFilterId || this.selectedChannelFilterId === 'all') {
        return this.items
      }

      const selectedId = Number(this.selectedChannelFilterId)

      return this.items.filter((item) => {
        if (!Array.isArray(item.channels)) return false

        return item.channels.some((channel) => Number(channel.id) === selectedId)
      })
    },

    groupedItems () {
      return this.filteredItems
        .map((item) => ({
          ...item,
          productionGroup: this.getProductionGroup(item.prodType),
        }))
        .sort((a, b) => {
          const groupCompare = String(a.productionGroup).localeCompare(String(b.productionGroup))

          if (groupCompare !== 0) return groupCompare

          const channelCountCompare =
            (Array.isArray(b.channels) ? b.channels.length : 0) -
            (Array.isArray(a.channels) ? a.channels.length : 0)

          if (channelCountCompare !== 0) return channelCountCompare

          return Number(b.id || 0) - Number(a.id || 0)
        })
    },
  },
  created () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.pageLoading = true
      this.$store.commit('setPageLoader')

      const params = {
        $orderby: '-id',
        'production[eq]': 1,
      }

      this.$store.dispatch('aepProjects', params)
        .then((projects) => {
          const list = projects || []

          const ids = list
            .map((item) => item.id)
            .filter((id) => id !== null && typeof id !== 'undefined')

          return Promise.all([
            Promise.resolve(list),
            this.$store.dispatch('channels', {
              $orderby: 'name',
            }),
            ids.length
              ? this.$store.dispatch('channelTemplates', {
                'aep.id[in]': ids.join(','),
              })
              : Promise.resolve([]),
          ])
        })
        .then((res) => {
          const list = res[0] || []
          const channels = this.normalizeChannels(res[1] || [])
          const channelTemplates = res[2] || []

          this.channels = channels

          if (
            this.selectedChannelFilterId !== 'all' &&
            !channels.some((channel) => Number(channel.id) === Number(this.selectedChannelFilterId))
          ) {
            this.selectedChannelFilterId = 'all'
          }

          if (!list.length) {
            this.items = []
            return
          }

          const channelMap = this.buildChannelMap(channels)
          const channelsByAepId = this.buildChannelsByAepId(
            channelTemplates,
            channelMap,
          )

          this.items = list.map((raw) => {
            const item = processAEPProject(raw)

            const prodType =
              item.prodType ||
              item.productionType ||
              (item.production && item.production.type) ||
              raw.prod_type ||
              raw.production_type ||
              ''

            const durationSeconds = Number(
              item.duration ||
              raw.duration ||
              (item.root && item.root.compDur ? item.root.compDur : 0),
            )

            const itemChannels = channelsByAepId[item.id] || []

            const prodTitle =
              item.prodTitle ||
              item.productionTitle ||
              item.title ||
              raw.prod_title ||
              raw.production_title ||
              item.name

            const prodDescription =
              item.prodDescription ||
              item.productionDescription ||
              item.description ||
              raw.prod_descr ||
              raw.prod_description ||
              raw.description ||
              ''

            const previewVideo =
              item.previewMp4 ||
              item.previewMP4 ||
              item.previewVideo ||
              item.previewVideoUrl ||
              item.preview_url ||
              item.previewUrl ||
              raw.preview_mp4 ||
              raw.preview_url ||
              raw.previewVideoUrl ||
              this.findDefaultMedia(item, ['Video', 'Media'])

            const previewPoster =
              item.pictureURL ||
              this.findDefaultMedia(item, ['Image'])

            const priority =
              typeof item.priority !== 'undefined' &&
              item.priority !== null
                ? Number(item.priority)
                : (
                  typeof raw.priority !== 'undefined' &&
                  raw.priority !== null
                    ? Number(raw.priority)
                    : null
                )

            return {
              ...item,
              durationSeconds,
              channels: itemChannels,
              prodTitle,
              prodDescription,
              prodType,
              productionGroup: this.getProductionGroup(prodType),
              priority,
              previewVideo,
              previewPoster,
              previewPlaying: false,

              // values only for v-data-table search
              idView: item.id,
              templateSearch: `${item.id} ${item.name} ${prodType} ${this.getLayersCount(item)} layers`,
              previewSearch: `${durationSeconds}`,
              productionSearch: `${prodTitle} ${prodDescription}`,
              channelsText: itemChannels.map((ch) => ch.name).join(' '),
            }
          })
        })
        .catch((errorMsg) => {
          this.$store.commit('showSnackbar', {
            text: errorMsg,
            timeout: 6000,
            color: 'error',
          })
        })
        .finally(() => {
          this.pageLoading = false
          this.$store.commit('resolvePageLoader')
        })
    },

    normalizeChannels (channels) {
      if (!Array.isArray(channels)) return []

      return channels
        .map((channel) => ({
          ...channel,
          id: channel.id,
          extid: channel.extid || '',
          name: channel.name || channel.title || channel.extid || `Channel #${channel.id}`,
          suspended: !!channel.suspended,
        }))
        .filter((channel) => channel.id)
    },

    getChannelTemplateAepId (item) {
      if (item.aep_id) return Number(item.aep_id)
      if (item.aepId) return Number(item.aepId)
      if (item.aep_project_id) return Number(item.aep_project_id)
      if (item.aepProjectId) return Number(item.aepProjectId)
      if (item.aep && item.aep.id) return Number(item.aep.id)
      if (item.project && item.project.id) return Number(item.project.id)

      return 0
    },

    getChannelTemplateChannelId (item) {
      if (item.channel_id) return Number(item.channel_id)
      if (item.channelId) return Number(item.channelId)
      if (item.channel && item.channel.id) return Number(item.channel.id)

      return 0
    },

    buildChannelMap (channels) {
      const map = {}

      for (let i = 0; i < channels.length; i += 1) {
        const channel = channels[i]

        if (!channel.id) continue

        map[Number(channel.id)] = channel
      }

      return map
    },

    buildChannelsByAepId (channelTemplates, channelMap) {
      const map = {}
      const positions = {}

      for (let i = 0; i < channelTemplates.length; i += 1) {
        const item = channelTemplates[i]

        const aepId = this.getChannelTemplateAepId(item)
        const channelId = this.getChannelTemplateChannelId(item)

        if (!aepId || !channelId) continue

        const channel = channelMap[channelId]

        if (!channel) continue

        const uniqueKey = `${aepId}_${channelId}`
        const selected = !!item.selected

        if (!map[aepId]) {
          map[aepId] = []
        }

        if (typeof positions[uniqueKey] !== 'undefined') {
          const existing = map[aepId][positions[uniqueKey]]

          if (selected && existing) {
            existing.selected = true
            existing.channelTemplateId = item.id
            existing.channelTemplateType = item.type || existing.channelTemplateType
          }

          continue
        }

        positions[uniqueKey] = map[aepId].length

        map[aepId].push({
          id: channel.id,
          extid: channel.extid || '',
          name: channel.name || channel.extid || `Channel #${channel.id}`,
          suspended: !!channel.suspended,
          selected,

          channelTemplateId: item.id,
          channelTemplateType: item.type || '',
        })
      }

      return map
    },

    getLayersCount (item) {
      return item && Array.isArray(item.props) ? item.props.length : 0
    },

    hasPriority (item) {
      return (
        item &&
        item.priority !== null &&
        typeof item.priority !== 'undefined' &&
        Number.isFinite(Number(item.priority))
      )
    },

    formatPriority (priority) {
      const labels = {
        0: 'Unclassified / lowest',
        2: 'No time constraint',
        4: 'Standard time constraint',
        6: 'High priority',
        8: 'Immediate / top priority',
      }

      const value = Number(priority)

      return labels[value] || 'Custom'
    },

    getProductionGroup (type) {
      const normalized = String(type || '')
        .trim()
        .toLowerCase()

      const groups = {
        announce: '01|Announce',
        intro: '02|Intro',
        bumper: '03|Bumper',
        'logo-reveal': '04|Logo Reveal',
        overlay: '05|Overlay',
        'live-start': '06|Live Start / Live End',
        'live-end': '06|Live Start / Live End',
        'channel-pitch': '07|Channel Pitch',
        'ad-placeholder': '08|Ad Placeholder',
        blackout: '09|Blackout',
      }

      if (groups[normalized]) {
        return groups[normalized]
      }

      if (normalized) {
        return `90|${this.formatProductionType(normalized)}`
      }

      return '99|Unclassified'
    },

    getProductionGroupLabel (group) {
      const value = String(group || '')
      const separator = value.indexOf('|')

      return separator === -1
        ? value
        : value.slice(separator + 1)
    },

    formatProductionType (type) {
      if (!type) return 'N/A'

      return String(type).trim().toUpperCase()
    },

    formatDuration (seconds) {
      const value = Number(seconds || 0)

      if (!value) {
        return '0.00 sec'
      }

      if (value < 60) {
        return `${value.toFixed(2)} sec`
      }

      const minutes = Math.floor(value / 60)
      const rest = Math.round(value % 60).toString().padStart(2, '0')

      return `${minutes}:${rest}`
    },

    getPreviewVideo (item) {
      return item.previewVideo || ''
    },

    getPreviewPoster (item) {
      return item.previewPoster || ''
    },

    playPreview (event, item) {
      const video = event.currentTarget.querySelector('video')

      if (!video) return

      this.$set(item, 'previewPlaying', true)

      const playPromise = video.play()

      if (playPromise && playPromise.catch) {
        playPromise.catch(() => {
          this.$set(item, 'previewPlaying', false)
        })
      }
    },

    stopPreview (event, item) {
      const video = event.currentTarget.querySelector('video')

      if (!video) return

      video.pause()
      video.currentTime = 0
      this.$set(item, 'previewPlaying', false)
    },

    findDefaultMedia (item, types) {
      if (!item || !Array.isArray(item.props)) return ''

      const normalizedTypes = types.map((type) => String(type).toLowerCase())

      const prop = item.props.find((p) => {
        const type = String(p.type || '').toLowerCase()

        return normalizedTypes.includes(type) && p.defaultValue
      })

      return prop ? prop.defaultValue : ''
    },

    getVisibleChannels (item) {
      return item.channels.slice(0, 12)
    },

    getHiddenChannels (item) {
      return item.channels.slice(12)
    },

    channelKey (channel) {
      return channel.id || channel.extid || channel.name
    },

    goToTemplateDetails (item) {
      this.$router.push({
        name: 'ProductionTemplateDetails',
        params: {
          template_id: item.id,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.production-title {
  padding: 22px 28px 18px;
  border-bottom: 1px solid #e8eaf0;
}

.production-heading {
  min-width: 0;
}

.production-subtitle {
  margin-top: 3px;
  font-size: 13px;
  line-height: 18px;
  color: #8a8f9c;
  font-weight: 400;
}

.production-actions {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.production-channel-filter {
  width: 240px;
  max-width: 240px;
}

.production-search {
  width: 360px;
  max-width: 360px;
}

.production-refresh-btn {
  flex: 0 0 auto;
}

::v-deep .production-table {
  box-shadow: none !important;
  border-radius: 0;

  .v-data-table__wrapper {
    overflow-x: hidden !important;
  }

  table {
    width: 100% !important;
    table-layout: fixed;
    border-collapse: collapse;
  }

  thead {
    tr {
      height: 54px;
    }

    th {
      font-size: 12px !important;
      line-height: 16px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #4f5363 !important;
      background: #fff;
      border-bottom: 1px solid #e8eaf0 !important;
      white-space: nowrap;
    }
  }

  tbody {
    tr {
      transition: background-color 0.15s ease;

      &:hover {
        background-color: #f7f8fb !important;
        cursor: pointer;
      }
    }

    td {
      min-width: 0;
      padding-top: 18px !important;
      padding-bottom: 18px !important;
      border-bottom: 1px solid #e8eaf0 !important;
      vertical-align: middle;
      overflow: hidden;
    }
  }
}

.template-id {
  font-size: 13px;
  font-weight: 600;
  color: #9aa0aa;
  white-space: nowrap;
}

.template-cell {
  min-width: 0;
}

.template-name {
  display: -webkit-box;
  max-width: 100%;
  overflow: hidden;
  font-size: 15px;
  line-height: 20px;
  font-weight: 700;
  color: #343848;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.template-meta {
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 17px;
}

.template-type {
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #6f7482;
}

.template-meta-separator {
  color: #b0b4be;
}

.template-layers {
  color: #8a8f9c;
}

.template-priority {
  margin-top: 2px;
  font-size: 12px;
  line-height: 17px;
  color: #8a8f9c;
}


/* Production type group header inside the table. */
::v-deep .production-table .v-row-group__header {
  background: #f7f8fa !important;
}

.production-group-cell {
  padding: 0 !important;
  border-top: 1px solid #e2e5eb !important;
  border-bottom: 1px solid #e2e5eb !important;
  background: #f7f8fa;
}

.production-group-toggle {
  width: 100%;
  min-height: 44px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  border: 0;
  outline: 0;
  background: transparent;
  color: #343848;
  font-family: inherit;
  font-size: 13px;
  line-height: 18px;
  font-weight: 800;
  letter-spacing: 0.045em;
  text-transform: uppercase;
  text-align: left;
  cursor: pointer;
}

.production-group-toggle:hover {
  background: #f1f3f6;
}

.preview-cell {
  width: 150px;
  max-width: 100%;
}

.preview-frame {
  position: relative;
  width: 140px;
  max-width: 100%;
  height: 84px;
  overflow: hidden;
  border-radius: 10px;
  background: #f0f2f5;
  border: 1px solid #e4e6eb;
}

.preview-video,
.preview-image,
.preview-poster {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-video,
.preview-image {
  display: block;
}

.preview-poster {
  position: absolute;
  inset: 0;
  z-index: 2;
}

.preview-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a8adb8;
}

.preview-play {
  position: absolute;
  z-index: 3;
  left: 50%;
  top: 50%;
  width: 34px;
  height: 34px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: rgba(20, 24, 35, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-duration {
  margin-top: 7px;
  font-size: 13px;
  line-height: 18px;
  color: #8a8f9c;
}

.production-copy {
  min-width: 0;
  max-width: 100%;
}

.production-copy-title {
  max-width: 100%;
  overflow: hidden;
  font-size: 15px;
  line-height: 20px;
  font-weight: 700;
  color: #343848;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.production-copy-desc {
  margin-top: 5px;
  display: -webkit-box;
  max-width: 100%;
  overflow: hidden;
  font-size: 13px;
  line-height: 19px;
  color: #6f7482;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.production-copy-empty {
  margin-top: 5px;
  font-size: 13px;
  line-height: 19px;
  color: #b0b4be;
}

.channels-cell {
  max-width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  overflow: hidden;
}

.channel-chip {
  max-width: 150px;
  height: 26px !important;
  padding: 0 9px !important;
  background: #f1f3f7 !important;
  color: #1a75d2 !important;
  font-size: 12px !important;
  font-weight: 600;
}

::v-deep .channel-chip .v-chip__content {
  max-width: 132px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.channel-chip-more {
  background: #e8f0ff !important;
  color: var(--v-primary-base) !important;
}

.channel-chip--selected {
  background: #e5f6ea !important;
  color: #238636 !important;
}

.channel-list-item--selected {
  background: #f0faf3 !important;
  color: #238636 !important;
}

.channels-empty {
  font-size: 13px;
  color: #b0b4be;
}

@media screen and (max-width: 1260px) {
  .production-title {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .production-actions {
    width: 100%;
    margin-top: 14px;
  }

  .production-channel-filter,
  .production-search {
    flex: 1 1 0;
    width: auto;
    max-width: none;
  }
}
</style>

